// src/context/SessionContext.jsx
import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { socket } from '../socket';

const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSessions = useCallback(async () => {
    try {
      // Lazy import to avoid circular deps
      const { default: api } = await import('../utils/axiosInstance');
      const res = await api.get('/api/sessions');
      const data = res?.data?.data ?? res?.data ?? [];
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      // Not logged in or network error
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();

    socket.on('session_update', fetchSessions);
    socket.on('connected', fetchSessions);
    socket.on('disconnected', fetchSessions);

    return () => {
      socket.off('session_update', fetchSessions);
      socket.off('connected', fetchSessions);
      socket.off('disconnected', fetchSessions);
    };
  }, [fetchSessions]);

  const connectedSessions = sessions.filter(s => s.status === 'connected');
  const hasActiveSession = connectedSessions.length > 0;

  return (
    <SessionContext.Provider value={{
      sessionId,
      setSessionId,
      sessions,
      connectedSessions,
      hasActiveSession,
      loading,
      refreshSessions: fetchSessions,
    }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
